/**
 * == Controller: InsightsCommentsController
 *
 * Handles the interactions inside the comments drawer that accompanies the
 * Insights log table. Provides a11y friendly collapse/expand controls and
 * mediates interactions from the inline composer so parent controllers can
 * react to submitted comments.
 *
 * === Responsibilities:
 * - Toggle the drawer between expanded/collapsed states and update the DOM
 *   to reflect the state for CSS and assistive tech.
 * - Normalise comment composer behaviour (Ctrl/Cmd + Enter for newlines,
 *   Enter to trigger submission).
 * - Dispatch a cancellable "submit" event with the composed payload so
 *   higher-level controllers can decide how/where to persist it.
 */
import PlatformBaseController from "./platform_base_controller";


export default class extends PlatformBaseController {
    static targets = ["collapseButton", "expandButton", "commentEditor", "logId", "tab", "threadRootId"];

    static values = {
        state: { type: String, default: "collapsed" },
        filter: { type: String, default: "open" }
    };



    connect() {
        if (super.connect) super.connect();
        this.applyState(this.stateValue);
        this.applyFilter(this.filterValue);
    }

    stateValueChanged(value) {
        this.applyState(value);
    }

    filterValueChanged(value) {
        this.applyFilter(value);
    }

    collapse(event) {
        event?.preventDefault();
        // Ignore collapse requests triggered after a comment submit so the panel stays open.
        if (event?.detail?.source === "comment-submit") return;
        this.stateValue = "collapsed";
    }

    toggle(event) {
        event?.preventDefault();
        if (this.stateValue === "expanded") {
            this.collapse();
        } else {
            this.expand();
        }
    }

    toggleExtend(event) {
        event?.preventDefault();
        if (this.stateValue === "expanded") {
            this.collapse();
            if (event?.isTrusted  && window.innerWidth > 1400 ) document.querySelector('.workspaces_show')?.click();
        } else {
            this.expand();
            if (event?.isTrusted  && window.innerWidth > 1400 ) document.querySelector('.workspaces_hide')?.click();
        }
    }

    expand(event) {
        event?.preventDefault();
        this.stateValue = "expanded";
        if (this.hasCommentEditorTarget) this.commentEditorTarget.focus();
    }

    applyState(state) {
        this.element.dataset.commentsState = state;
        this.element.setAttribute("aria-expanded", state === "expanded" ? "true" : "false");

        const collapsed = state === "collapsed";
        this.element.hidden = collapsed;
        if (this.hasCollapseButtonTarget) this.collapseButtonTarget.hidden = collapsed;
        if (this.hasExpandButtonTarget) this.expandButtonTarget.hidden = !collapsed;
        if (collapsed) {
            window.dispatchEvent(new CustomEvent("insights:hide-comments"));
        }
    }

    filter(event) {
        event?.preventDefault();
        const target = event?.currentTarget;
        const nextFilter = target?.dataset?.filter;
        if (!nextFilter || nextFilter === this.filterValue) return;
        this.filterValue = nextFilter;
        this.loadFilteredComments(nextFilter);
    }

    applyFilter(filter = this.filterValue || "open") {
        this.tabTargets.forEach((tab) => {
            const isActive = tab.dataset.filter === filter;
            tab.dataset.state = isActive ? "active" : "inactive";
            tab.setAttribute("aria-pressed", isActive ? "true" : "false");
        });

    }

    refresh(event) {
        const logId = event?.detail?.logId;
        const currentId = this.currentLogId();
        if (!logId || !currentId || String(logId) !== String(currentId)) return;
        this.loadFilteredComments(this.filterValue || "open");
    }

    loadFilteredComments(filter) {
        const logId = this.currentLogId();
        if (!logId) return;

        const url = new URL("/insights/show_comments", window.location.origin);
        url.searchParams.set("log_id", logId);
        url.searchParams.set("filter", filter);
        url.searchParams.set("list_only", "1");

        this.fetchAction(url.toString());
    }

    insertNewline(event) {
        event.preventDefault();
        const el = event.target;
        if (typeof el.selectionStart === "number") {
            const start = el.selectionStart;
            const end = el.selectionEnd;
            const value = el.value || "";
            el.value = `${value.slice(0, start)}\n${value.slice(end)}`;
            el.selectionStart = el.selectionEnd = start + 1;
            return;
        }

        if (document.queryCommandSupported?.("insertText")) {
            document.execCommand("insertText", false, "\n");
        } else if ("textContent" in el) {
            el.textContent = `${el.textContent || ""}\n`;
        }
    }

    ask(event) {
        event?.preventDefault();
        if (!this.hasCommentEditorTarget || this.submitting) return;

        const body = this.currentEditorValue().trim();
        const logId = this.currentLogId();
        if (!body.length || !logId) return;

        const parentId = this.currentThreadRootId();
        const detail = { logId, body, parentId };
        const dispatched = this.dispatch("submit", { detail, cancelable: true });
        if (dispatched?.defaultPrevented) return;

        this.setEditorValue("");
        this.submitComment(detail);
    }

    showThread(event) {
        event?.preventDefault();
        const commentId = event.currentTarget.dataset.commentId;
        const logId = this.currentLogId();
        if (!commentId || !logId) return;

        const url = new URL("/insights/show_comments", window.location.origin);
        url.searchParams.set("log_id", logId);
        url.searchParams.set("thread_id", commentId);
        url.searchParams.set("list_only", "1");
        url.searchParams.set("filter", this.filterValue || "open");

        this.fetchAction(url.toString());
    }

    backToList(event) {
        event?.preventDefault();
        this.loadFilteredComments(this.filterValue || "open");
    }

    currentEditorValue() {
        if (!this.hasCommentEditorTarget) return "";
        const el = this.commentEditorTarget;
        if ("value" in el) return el.value || "";
        return el.textContent || "";
    }

    setEditorValue(value) {
        if (!this.hasCommentEditorTarget) return;
        const el = this.commentEditorTarget;
        if ("value" in el) {
            el.value = value;
        } else {
            el.textContent = value;
        }
    }

    currentLogId() {
        if (!this.hasLogIdTarget) return undefined;
        return this.logIdTarget.value;
    }

    currentThreadRootId() {
        if (!this.hasThreadRootIdTarget) return null;
        return this.threadRootIdTarget.value || null;
    }

    submitComment({ logId, body, parentId = null }) {
        if (!logId || !body) return;

        const formData = new FormData();
        formData.append("log_id", logId);
        formData.append("body", body);
        if (parentId) formData.append("parent_id", parentId);

        const token = document.querySelector('meta[name="csrf-token"]')?.content;

        this.submitting = true;
        return this.fetchAction("/insights/comments", {
            method: "post",
            headers: token ? { "X-CSRF-Token": token } : {},
            body: formData,
            onSuccess: () => window.dispatchEvent(new CustomEvent("insights:show-comments", { detail: { logId, source: "comment-submit" } }))
        }).finally(() => {
            this.submitting = false;
        });
    }
}
